"use client";

// Real document components (the same ones the app and its PDFs use) rendered with
// sample data, for the home page and guide.

import { useEffect, useRef, useState } from "react";
import InvoicePreview from "@/components/Invoice/InvoicePreview";
import LetterPreview from "@/components/Invoice/LetterPreview";
import { asset } from "./site";

export const SAMPLE_BRAND = {
  id: "demo",
  name: "Aurora Solar & Electricals",
  tagline: "Rooftop solar · EPC · AMC",
  address: "14, Industrial Area, Sitapura",
  city: "Jaipur",
  state: "Rajasthan",
  postcode: "302022",
  country: "India",
  gstin: "08AAPFA1234K1Z5",
  pan: "AAPFA1234K",
  regLabel: "MSME",
  regNo: "UDYAM-RJ-17-0012345",
  email: "hello@aurorasolar.in",
  phone: "+91 98290 12345",
  website: "www.aurorasolar.in",
  logo: asset("demo-logo.png"),
  signature: asset("demo-signature.png"),
  stamp: asset("demo-stamp.png"),
  signatory: "Authorised Signatory",
  accent: "#0F766E",
  template: "standard",
  bank: {},
  bankInr: { accountName: "AURORA SOLAR & ELECTRICALS", accountNumber: "50200012345678", ifsc: "HDFC0001234", bankName: "HDFC Bank", branch: "Sitapura, Jaipur" },
};

const DATA = { settings: { defaultBrandId: "demo", defaults: { dateStyle: "pretty", showQr: false } }, brands: [SAMPLE_BRAND], clients: [] };

const line = (description, rate, extra = {}) => ({ id: description, description, rate, qty: 1, taxRate: 18, ...extra });

export const SAMPLE_QUOTE = {
  docType: "quote",
  number: "AS/QT/2026-27/14",
  date: "2026-10-08",
  dueDate: "2026-10-23",
  currency: "INR",
  taxMode: "intra",
  brandId: "demo",
  manualOrder: true,
  taxInclusive: true,
  showHsn: true,
  copyLabel: "ORIGINAL FOR RECIPIENT",
  placeOfSupply: "Rajasthan (08)",
  subject: "Quotation for 5 kW rooftop solar system",
  billTo: { name: "Mrs Pinki Sharma", address: "B-4, Malviya Nagar", city: "Jaipur", state: "Rajasthan", phone: "9057223544" },
  items: [
    line("On-grid solar power system 5 kW", 262500, { taxRate: 12, hsn: "85414300", section: "Supply", details: "Mono-PERC modules 540 Wp × 10, 5 kW inverter\nACDB + DCDB, earthing kit, GI structure" }),
    line("Installation & commissioning", 25000, { hsn: "995461", section: "Installation", details: "Site survey, testing, net-meter coordination" }),
    line("Panel cleaning AMC – 3 years", 18000, { section: "Add-ons", optional: true }),
  ],
  schedule: [
    { id: "a", label: "Advance with purchase order", percent: 25 },
    { id: "b", label: "Before dispatch of material", percent: 70 },
    { id: "c", label: "On commissioning", percent: 5 },
  ],
  notes: "Products carry the manufacturers' warranties.",
};

const SAMPLE_INVOICE = {
  ...SAMPLE_QUOTE,
  docType: "invoice",
  number: "AS/2026-27/27",
  schedule: [],
  subject: "",
  items: SAMPLE_QUOTE.items.filter((l) => !l.optional),
};

// One A4 page scaled down to `width` px – or, with `fluid`, to the width of its column.
export function Paper({ width = 360, fluid, children }) {
  const ref = useRef(null);
  const [w, setW] = useState(width);
  useEffect(() => {
    if (!fluid || !ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(Math.min(width, e.contentRect.width)));
    ro.observe(ref.current.parentElement);
    return () => ro.disconnect();
  }, [fluid, width]);
  const s = w / 794;
  return (
    <div ref={ref} className="relative overflow-hidden rounded-lg bg-white shadow-[0_30px_60px_-25px_rgba(15,23,42,0.45)] ring-1 ring-slate-900/10" style={{ width: w, height: 1123 * s }}>
      <div className="pointer-events-none absolute left-0 top-0 origin-top-left" style={{ transform: `scale(${s})` }}>
        {children}
      </div>
    </div>
  );
}

export function LiveQuote({ width = 380, template = "standard" }) {
  return (
    <Paper width={width}>
      <InvoicePreview invoice={{ ...SAMPLE_QUOTE, template }} data={DATA} thumbnail />
    </Paper>
  );
}

const TEMPLATE_SET = [
  ["executive", "Executive", "Premium"],
  ["standard", "GST Standard", "Simple"],
  ["royal", "Royal", "Premium"],
  ["modern", "Modern", "Premium"],
  ["clean", "Clean", "Simple"],
  ["classic", "Classic", "Simple"],
];

export function TemplateGallery() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5">
      {TEMPLATE_SET.map(([key, label, tier]) => (
        <figure key={key} className="flex min-w-0 flex-col items-center">
          <Paper width={230} fluid>
            <InvoicePreview invoice={{ ...SAMPLE_INVOICE, template: key }} data={DATA} thumbnail />
          </Paper>
          <figcaption className="mt-3 text-center text-sm font-medium text-slate-800">
            {label} <span className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${tier === "Premium" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{tier}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

const SAMPLE_LETTER = {
  number: "AS/L/2026-27/3",
  date: "2026-10-08",
  to: { name: "Green Valley School", lines: "Ajmer, Rajasthan", attention: "The Principal" },
  subject: "Our quotation for a 30 kW rooftop solar plant",
  salutation: "Dear Sir / Madam,",
  body: "Thank you for the opportunity to submit our offer.\n\nPlease find enclosed our quotation for a 30 kW rooftop solar plant for your campus, covering supply, installation, net-metering and five years of maintenance visits.\n\nWe would be glad to walk you through the proposal at your convenience.",
  closing: "Yours faithfully,",
  signName: "Arjun Mathur",
  signTitle: "Director",
  showQr: false,
};

export function LetterGallery({ designs = ["executive", "wave", "corporate", "royal"] }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
      {designs.map((d) => (
        <div key={d} className="flex min-w-0 justify-center">
          <Paper width={230} fluid>
            <LetterPreview letter={{ ...SAMPLE_LETTER, design: d }} brand={SAMPLE_BRAND} design={d} thumbnail />
          </Paper>
        </div>
      ))}
    </div>
  );
}
